import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import draftContractService from '../../services/draftContractService'
import customerMasterService from '../../services/customerMasterService'
import projectService from '../../services/projectService'
import { useContractTypeOptions } from './useContractTypeOptions'

const DraftContractForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const contractData = location.state?.contractData

  const [formData, setFormData] = useState({
    contractNumber: '',
    contractTitle: '',
    contractDescription: '',
    contractType: 'Service Agreement',
    partyType: 'Customer',
    party: '',
    partyName: '',
    partyCode: '',
    partyContactPerson: '',
    partyEmail: '',
    partyAddress: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    proposedStartDate: '',
    proposedEndDate: '',
    proposedDuration: 0,
    contractValue: 0,
    currency: 'INR',
    paymentTerms: '',
    billingFrequency: 'Monthly',
    project: '',
    projectCode: '',
    projectName: '',
    termsAndConditions: '',
    keyTerms: [],
    deliverables: [],
    milestones: [],
    legalReviewRequired: false,
    complianceRequirements: [],
    priority: 'Medium',
    notes: '',
    internalNotes: '',
    status: 'Draft'
  })

  const [customers, setCustomers] = useState([])
  const [projects, setProjects] = useState([])
  const [isLoadingContractNumber, setIsLoadingContractNumber] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newKeyTerm, setNewKeyTerm] = useState({ term: '', description: '' })
  const [newDeliverable, setNewDeliverable] = useState('')
  const [newMilestone, setNewMilestone] = useState({ milestoneName: '', milestoneDate: '', milestoneAmount: 0, description: '' })
  const [newCompliance, setNewCompliance] = useState('')
  const { options: contractTypeOptions } = useContractTypeOptions(formData.contractType)

  // Fetch next contract number (only for new contracts)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchContractNumber = async () => {
        try {
          setIsLoadingContractNumber(true)
          const response = await draftContractService.getNextContractNumber()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              contractNumber: response.data.contractNumber || response.data.formattedValue || 'DC0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching contract number:', error)
          setFormData(prev => ({ ...prev, contractNumber: 'DC0001' }))
        } finally {
          setIsLoadingContractNumber(false)
        }
      }
      fetchContractNumber()
    }
  }, [isEditMode, isViewMode])

  // Load customers and projects
  useEffect(() => {
    loadCustomers()
    loadProjects()
  }, [])

  // Load contract data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && contractData?._id) {
      loadContractData(contractData._id)
    }
  }, [isEditMode, isViewMode, contractData])

  const loadCustomers = async () => {
    try {
      const response = await customerMasterService.getAllCustomers({ limit: 1000 })
      if (response.success) {
        setCustomers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading customers:', error)
    }
  }

  const loadProjects = async () => {
    try {
      const response = await projectService.getAllProjects({ limit: 1000 })
      if (response.success) {
        setProjects(response.data || [])
      }
    } catch (error) {
      console.error('Error loading projects:', error)
    }
  }

  const loadContractData = async (id) => {
    try {
      setIsLoading(true)
      const response = await draftContractService.getDraftContractById(id)
      if (response.success && response.data) {
        const contract = response.data
        setFormData({
          contractNumber: contract.contractNumber || '',
          contractTitle: contract.contractTitle || '',
          contractDescription: contract.contractDescription || '',
          contractType: contract.contractType || 'Service Agreement',
          partyType: contract.partyType || 'Customer',
          party: contract.party?._id || contract.party || '',
          partyName: contract.partyName || contract.party?.customerName || '',
          partyCode: contract.partyCode || contract.party?.customerCode || '',
          partyContactPerson: contract.partyContactPerson || '',
          partyEmail: contract.partyEmail || contract.party?.email || '',
          partyAddress: contract.partyAddress || {
            street: '',
            city: '',
            state: '',
            country: '',
            zipCode: ''
          },
          proposedStartDate: contract.proposedStartDate ? new Date(contract.proposedStartDate).toISOString().split('T')[0] : '',
          proposedEndDate: contract.proposedEndDate ? new Date(contract.proposedEndDate).toISOString().split('T')[0] : '',
          proposedDuration: contract.proposedDuration || 0,
          contractValue: contract.contractValue || 0,
          currency: contract.currency || 'INR',
          paymentTerms: contract.paymentTerms || '',
          billingFrequency: contract.billingFrequency || 'Monthly',
          project: contract.project?._id || contract.project || '',
          projectCode: contract.projectCode || contract.project?.projectCode || '',
          projectName: contract.projectName || contract.project?.projectName || '',
          termsAndConditions: contract.termsAndConditions || '',
          keyTerms: contract.keyTerms || [],
          deliverables: contract.deliverables || [],
          milestones: contract.milestones || [],
          legalReviewRequired: contract.legalReviewRequired || false,
          complianceRequirements: contract.complianceRequirements || [],
          priority: contract.priority || 'Medium',
          notes: contract.notes || '',
          internalNotes: contract.internalNotes || '',
          status: contract.status || 'Draft'
        })
      }
    } catch (error) {
      console.error('Error loading contract data:', error)
      toast.error('Failed to load contract data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('partyAddress.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        partyAddress: {
          ...prev.partyAddress,
          [addressField]: value
        }
      }))
    } else if (name === 'party') {
      const selectedCustomer = customers.find(cust => cust._id === value)
      setFormData(prev => ({
        ...prev,
        party: value,
        partyName: selectedCustomer?.customerName || '',
        partyCode: selectedCustomer?.customerCode || '',
        partyEmail: selectedCustomer?.email || '',
        partyAddress: selectedCustomer?.address || prev.partyAddress
      }))
    } else if (name === 'project') {
      const selectedProject = projects.find(proj => proj._id === value)
      setFormData(prev => ({
        ...prev,
        project: value,
        projectCode: selectedProject?.projectNumber || '',
        projectName: selectedProject?.projectName || ''
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }

    // Calculate duration if dates changed
    if (name === 'proposedStartDate' || name === 'proposedEndDate') {
      const startDate = name === 'proposedStartDate' ? value : formData.proposedStartDate
      const endDate = name === 'proposedEndDate' ? value : formData.proposedEndDate
      if (startDate && endDate) {
        const start = new Date(startDate)
        const end = new Date(endDate)
        const diffTime = Math.abs(end - start)
        const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30))
        setFormData(prev => ({
          ...prev,
          proposedDuration: diffMonths
        }))
      }
    }
  }

  const addKeyTerm = () => {
    if (newKeyTerm.term.trim()) {
      setFormData(prev => ({
        ...prev,
        keyTerms: [...prev.keyTerms, { ...newKeyTerm }]
      }))
      setNewKeyTerm({ term: '', description: '' })
    }
  }

  const removeKeyTerm = (index) => {
    setFormData(prev => ({
      ...prev,
      keyTerms: prev.keyTerms.filter((_, i) => i !== index)
    }))
  }

  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setFormData(prev => ({
        ...prev,
        deliverables: [...prev.deliverables, newDeliverable.trim()]
      }))
      setNewDeliverable('')
    }
  }

  const removeDeliverable = (index) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }))
  }

  const addMilestone = () => {
    if (newMilestone.milestoneName.trim()) {
      setFormData(prev => ({
        ...prev,
        milestones: [...prev.milestones, { ...newMilestone }]
      }))
      setNewMilestone({ milestoneName: '', milestoneDate: '', milestoneAmount: 0, description: '' })
    }
  }

  const removeMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }))
  }

  const addCompliance = () => {
    if (newCompliance.trim()) {
      setFormData(prev => ({
        ...prev,
        complianceRequirements: [...prev.complianceRequirements, newCompliance.trim()]
      }))
      setNewCompliance('')
    }
  }

  const removeCompliance = (index) => {
    setFormData(prev => ({
      ...prev,
      complianceRequirements: prev.complianceRequirements.filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.contractTitle || !formData.partyName) {
        toast.error('Please fill in all required fields (Contract Title and Party)')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && contractData?._id) {
        const response = await draftContractService.updateDraftContract(contractData._id, formData)
        if (response.success) {
          toast.success('Draft contract updated successfully!')
          navigate('/contracts/draft-contract')
        } else {
          toast.error(response.message || 'Failed to update draft contract')
        }
      } else {
        const response = await draftContractService.createDraftContract(formData)
        if (response.success) {
          toast.success('Draft contract created successfully!')
          navigate('/contracts/draft-contract')
        } else {
          toast.error(response.message || 'Failed to create draft contract')
        }
      }
    } catch (error) {
      console.error('Error saving draft contract:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save draft contract')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadContractData(contractData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        contractTitle: '',
        contractDescription: '',
        contractType: 'Service Agreement',
        partyType: 'Customer',
        party: '',
        partyName: '',
        partyCode: '',
        partyContactPerson: '',
        partyEmail: '',
        partyAddress: {
          street: '',
          city: '',
          state: '',
          country: '',
          zipCode: ''
        },
        proposedStartDate: '',
        proposedEndDate: '',
        proposedDuration: 0,
        contractValue: 0,
        paymentTerms: '',
        project: '',
        projectCode: '',
        projectName: '',
        termsAndConditions: '',
        keyTerms: [],
        deliverables: [],
        milestones: [],
        legalReviewRequired: false,
        complianceRequirements: [],
        priority: 'Medium',
        notes: '',
        internalNotes: '',
        status: 'Draft'
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Draft Contracts', path: '/contracts/draft-contract' },
    { 
      label: isEditMode ? 'Edit Draft Contract' : isViewMode ? 'View Draft Contract' : 'New Draft Contract', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading contract...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Draft Contract' : isViewMode ? 'View Draft Contract' : 'New Draft Contract'}</h1>
          <p>{isEditMode ? 'Update draft contract details' : isViewMode ? 'View draft contract details' : 'Create a new draft contract'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/contracts/draft-contract')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Contract Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Contract Details</h3>
          </div>

          {/* Row 1: Contract Number, Contract Title, Contract Type, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="contractNumber" className="form-label required">
                Contract Number
              </label>
              <input
                type="text"
                id="contractNumber"
                name="contractNumber"
                value={isLoadingContractNumber ? 'Generating...' : formData.contractNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="contractTitle" className="form-label required">
                Contract Title
              </label>
              <input
                type="text"
                id="contractTitle"
                name="contractTitle"
                value={formData.contractTitle}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., Service Agreement with ABC Corp"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contractType" className="form-label">
                Contract Type
              </label>
              <select
                id="contractType"
                name="contractType"
                value={formData.contractType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                {contractTypeOptions.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Draft">Draft</option>
                <option value="Under Review">Under Review</option>
                <option value="Legal Review">Legal Review</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Ready for Signature">Ready for Signature</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Contract Description */}
          <div className="form-group">
            <label htmlFor="contractDescription" className="form-label">
              Contract Description
            </label>
            <textarea
              id="contractDescription"
              name="contractDescription"
              value={formData.contractDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Brief description of the contract..."
            />
          </div>

          {/* Party Information Section */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Party Information</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="partyType" className="form-label">
                Party Type
              </label>
              <select
                id="partyType"
                name="partyType"
                value={formData.partyType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Customer">Customer</option>
                <option value="Vendor">Vendor</option>
                <option value="Partner">Partner</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="party" className="form-label required">
                Party
              </label>
              <select
                id="party"
                name="party"
                value={formData.party}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              >
                <option value="">Select {formData.partyType}</option>
                {customers.map(cust => (
                  <option key={cust._id} value={cust._id}>
                    {cust.customerName} ({cust.customerCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="partyContactPerson" className="form-label">
                Contact Person
              </label>
              <input
                type="text"
                id="partyContactPerson"
                name="partyContactPerson"
                value={formData.partyContactPerson}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Contact person name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="partyEmail" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="partyEmail"
                name="partyEmail"
                value={formData.partyEmail}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="party@example.com"
              />
            </div>
          </div>

          {/* Party Address */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="partyAddress.street" className="form-label">
                Street
              </label>
              <input
                type="text"
                id="partyAddress.street"
                name="partyAddress.street"
                value={formData.partyAddress.street}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="partyAddress.city" className="form-label">
                City
              </label>
              <input
                type="text"
                id="partyAddress.city"
                name="partyAddress.city"
                value={formData.partyAddress.city}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="partyAddress.state" className="form-label">
                State
              </label>
              <input
                type="text"
                id="partyAddress.state"
                name="partyAddress.state"
                value={formData.partyAddress.state}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="partyAddress.country" className="form-label">
                Country
              </label>
              <input
                type="text"
                id="partyAddress.country"
                name="partyAddress.country"
                value={formData.partyAddress.country}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>
          </div>

          {/* Contract Dates and Duration */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Contract Period</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="proposedStartDate" className="form-label">
                Proposed Start Date
              </label>
              <input
                type="date"
                id="proposedStartDate"
                name="proposedStartDate"
                value={formData.proposedStartDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="proposedEndDate" className="form-label">
                Proposed End Date
              </label>
              <input
                type="date"
                id="proposedEndDate"
                name="proposedEndDate"
                value={formData.proposedEndDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="proposedDuration" className="form-label">
                Duration (Months)
              </label>
              <input
                type="number"
                id="proposedDuration"
                name="proposedDuration"
                value={formData.proposedDuration}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="priority" className="form-label">
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Financial Information */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Financial Information</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="contractValue" className="form-label">
                Contract Value
              </label>
              <input
                type="number"
                id="contractValue"
                name="contractValue"
                value={formData.contractValue}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency" className="form-label">
                Currency
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="billingFrequency" className="form-label">
                Billing Frequency
              </label>
              <select
                id="billingFrequency"
                name="billingFrequency"
                value={formData.billingFrequency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="One-time">One-time</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
                <option value="Milestone-based">Milestone-based</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="project" className="form-label">
                Project
              </label>
              <select
                id="project"
                name="project"
                value={formData.project}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="">Select Project</option>
                {projects.map(proj => (
                  <option key={proj._id} value={proj._id}>
                    {proj.projectName} ({proj.projectNumber})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="paymentTerms" className="form-label">
              Payment Terms
            </label>
            <textarea
              id="paymentTerms"
              name="paymentTerms"
              value={formData.paymentTerms}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="2"
              placeholder="e.g., Net 30, 50% advance, etc."
            />
          </div>

          {/* Key Terms */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Key Terms</h3>
            </div>
          </div>

          {formData.keyTerms.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.keyTerms.map((term, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginBottom: '8px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <strong>{term.term}</strong>
                    {term.description && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '4px' }}>{term.description}</div>}
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => removeKeyTerm(index)}
                      style={{
                        padding: '6px',
                        border: 'none',
                        background: '#dc3545',
                        color: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Term name"
                value={newKeyTerm.term}
                onChange={(e) => setNewKeyTerm({ ...newKeyTerm, term: e.target.value })}
                className="form-input"
                style={{ flex: 1 }}
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={newKeyTerm.description}
                onChange={(e) => setNewKeyTerm({ ...newKeyTerm, description: e.target.value })}
                className="form-input"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={addKeyTerm}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          )}

          {/* Deliverables */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Deliverables</h3>
            </div>
          </div>

          {formData.deliverables.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.deliverables.map((deliverable, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginBottom: '8px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>{deliverable}</div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => removeDeliverable(index)}
                      style={{
                        padding: '6px',
                        border: 'none',
                        background: '#dc3545',
                        color: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Enter deliverable"
                value={newDeliverable}
                onChange={(e) => setNewDeliverable(e.target.value)}
                className="form-input"
                style={{ flex: 1 }}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDeliverable())}
              />
              <button
                type="button"
                onClick={addDeliverable}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          )}

          {/* Milestones */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Milestones</h3>
            </div>
          </div>

          {formData.milestones.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.milestones.map((milestone, index) => (
                <div key={index} style={{ 
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  marginBottom: '8px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <strong>{milestone.milestoneName}</strong>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removeMilestone(index)}
                        style={{
                          padding: '6px',
                          border: 'none',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
                    {milestone.milestoneDate && `Date: ${new Date(milestone.milestoneDate).toLocaleDateString()}`}
                    {milestone.milestoneAmount > 0 && ` | Amount: ${new Intl.NumberFormat('en-IN', { style: 'currency', currency: formData.currency }).format(milestone.milestoneAmount)}`}
                    {milestone.description && ` | ${milestone.description}`}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Milestone Name"
                  value={newMilestone.milestoneName}
                  onChange={(e) => setNewMilestone({ ...newMilestone, milestoneName: e.target.value })}
                  className="form-input"
                />
                <input
                  type="date"
                  placeholder="Date"
                  value={newMilestone.milestoneDate}
                  onChange={(e) => setNewMilestone({ ...newMilestone, milestoneDate: e.target.value })}
                  className="form-input"
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={newMilestone.milestoneAmount}
                  onChange={(e) => setNewMilestone({ ...newMilestone, milestoneAmount: parseFloat(e.target.value) || 0 })}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
                <input
                  type="text"
                  placeholder="Description"
                  value={newMilestone.description}
                  onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                  className="form-input"
                />
              </div>
              <button
                type="button"
                onClick={addMilestone}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Milestone
              </button>
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="form-group">
            <label htmlFor="termsAndConditions" className="form-label">
              Terms and Conditions
            </label>
            <textarea
              id="termsAndConditions"
              name="termsAndConditions"
              value={formData.termsAndConditions}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="5"
              placeholder="Enter contract terms and conditions..."
            />
          </div>

          {/* Legal and Compliance */}
          <div className="form-section-divider">
            <div className="section-header">
              <h3>Legal and Compliance</h3>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="legalReviewRequired" className="form-label">
                Legal Review Required
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="legalReviewRequired"
                  name="legalReviewRequired"
                  checked={formData.legalReviewRequired}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="legalReviewRequired" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Requires legal review
                </label>
              </div>
            </div>
          </div>

          {/* Compliance Requirements */}
          {formData.complianceRequirements.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <label className="form-label">Compliance Requirements</label>
              {formData.complianceRequirements.map((req, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginBottom: '8px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>{req}</div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => removeCompliance(index)}
                      style={{
                        padding: '6px',
                        border: 'none',
                        background: '#dc3545',
                        color: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Enter compliance requirement"
                value={newCompliance}
                onChange={(e) => setNewCompliance(e.target.value)}
                className="form-input"
                style={{ flex: 1 }}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCompliance())}
              />
              <button
                type="button"
                onClick={addCompliance}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="General notes..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="internalNotes" className="form-label">
              Internal Notes
            </label>
            <textarea
              id="internalNotes"
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Internal notes (not visible to party)..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default DraftContractForm

